import type React from "react";

import styled, { css } from "styled-components";
import type { LocalParticipant } from "twilio-video";

import { AudioLevelIndicator } from "../AudioLevelIndicator";
import { DisabledPublicationsOverlay } from "../DisabledPublicationsOverlay";
import { usePublications } from "../video/hooks/usePublications";

import { MainPublication } from "./MainPublication";

interface MainParticipantProps {
  readonly audioLevel?: number;
  readonly audioWarning?: boolean;
  readonly minimised: boolean;
  readonly participant?: LocalParticipant | null;
  readonly hasRemoteParticipantConnected: boolean;
  readonly videoEnabled: boolean;
  readonly audioEnabled: boolean;
  readonly username: string;
}

export const MainParticipant: React.FC<MainParticipantProps> = ({
  audioLevel = 0,
  audioWarning = false,
  minimised,
  participant = null,
  hasRemoteParticipantConnected,
  audioEnabled,
  videoEnabled,
  username,
}) => {
  const publications = usePublications(participant);

  if (participant && publications.length > 0) {
    return (
      <MyVideoScreen $hasRemoteParticipantConnected={hasRemoteParticipantConnected}>
        {publications.map(publication => {
          return (
            <MainPublication
              key={`${publication.kind}_${publication.trackSid}`}
              publication={publication}
              videoEnabled={videoEnabled}
              audioEnabled={audioEnabled}
            />
          );
        })}

        {minimised && (
          <DisabledPublicationsOverlay
            kinds={["video"]}
            minimised={false}
            publications={publications}
            username={username}
          />
        )}
        {!minimised && !hasRemoteParticipantConnected && (
          <AudioLevelIndicator audioLevel={audioLevel} hasWarning={audioWarning} position="local" />
        )}
        {hasRemoteParticipantConnected && (
          <AudioLevelIndicator audioLevel={audioLevel} hasWarning={audioWarning} position="local" />
        )}
      </MyVideoScreen>
    );
  }

  return null;
};

type MyVideoScreenProps = {
  $hasRemoteParticipantConnected: boolean;
};

const MyVideoScreen = styled.div<MyVideoScreenProps>`
  display: flex;
  flex: 1;
  justify-content: center;
  width: 100%;
  position: relative;

  ${props =>
    props.$hasRemoteParticipantConnected &&
    css`
      background-color: #3c4c60;
      position: absolute;
      top: 20px;
      right: 20px;
      border-radius: ${props.theme.borderRadius.basic};
      box-shadow: 2px 4px 11px rgba(29, 44, 62, 0.2);
      overflow: hidden;
      width: 120px;
      height: 160px;
      z-index: 1;
    `}
`;
